import React, {useEffect, useState} from 'react';
import {useRouter} from 'next/router';

import {ProductVariant, useManagePlanQuery, useProductDetailsQuery} from '#apollo/schema';
import {Text} from '#component/base';
import RectangleSkeleton from '#component/base/skeletons/RectangleSkeleton';
import {
  EventType,
  ModalViewEvent,
  PageViewEvent,
  useAnalytics,
} from '#component/provider/AnalyticsProvider';
import {supplements} from '#constant/products';
import {useViewportSize} from '#hook';
import {SubscriptionStatusEnum} from '#util/subscriptions';

import AdditionalProducts from './AdditionalProducts/AdditionalProducts';
import {CancelationModal} from './CancelationModal/CancelationModal';
import {SubscriptionManagerCard} from './components/SubscriptionManagerCard';
import {
  getHighestPricedSupplementTid,
  parseQueryParams,
  transformVariantToProduct,
} from './SubscriptionManager.util';
import {useSubscriptionManager} from './SubscriptionManagerContext';

import {
  ManageProductType,
  PromoCodeAction,
  SubscriptionManagerState,
} from './SubscriptionManager.types';

import * as css from './SubscriptionManager.css';

const LoadingSkeleton = () => {
  const {isTablet, isDesktop} = useViewportSize();
  return (
    <div className={css.SubscriptionCardContainer}>
      <RectangleSkeleton width={600} height={400} />
      {(isTablet || isDesktop) && <RectangleSkeleton width={600} height={400} />}
    </div>
  );
};

export const SubscriptionManager = () => {
  const {track} = useAnalytics();
  const {data, loading} = useManagePlanQuery();
  const {subscription} = data?.me?.customerAccount ?? {};

  const hasAppliedDiscountCancelation = data?.me?.appliedDiscountCancelationOn ? true : false;

  const [openCancelationModal, setOpenCancelationModal] = useState(false);

  const {data: productData, loading: productLoading} = useProductDetailsQuery();
  const {productVariants} = productData?.store || {};

  const {
    initialState,
    setInitialState,
    pendingChanges,
    setPendingChanges,
    getInitialSubscriptionManagerState,
    onChangeSubscription,
    updatePromoCodes,
    setSelectedSupplements,
    setOpenUpdateConfirmationModal,
    setHasAutoAppliedPromoCodes,
    setAlreadyAppliedPromoCodeError,
  } = useSubscriptionManager();

  const hasActiveSubscriptionOrMembership =
    (subscription?.hasActiveSubscription ||
      subscription?.hasMembership ||
      subscription?.status === SubscriptionStatusEnum.PAUSED) &&
    !(subscription?.status === SubscriptionStatusEnum.CANCELLED);

  const router = useRouter();

  useEffect(() => {
    track<PageViewEvent>({
      evtName: EventType.PageView,
      pageName: 'View Manage My Plan',
    });
  }, [track]);

  useEffect(() => {
    const initialState = getInitialSubscriptionManagerState({
      subscription: subscription || {},
      productVariants: productVariants || [],
    });
    setInitialState(initialState);
    setPendingChanges(initialState);
  }, [
    subscription,
    productVariants,
    setInitialState,
    setPendingChanges,
    getInitialSubscriptionManagerState,
  ]);

  useEffect(() => {
    const {add, discount} = parseQueryParams(router.query);

    if (!hasActiveSubscriptionOrMembership) {
      if (add && discount && productVariants) {
        const productIds = add.split(',');
        const validProductsIds = productIds.filter(id => productVariants.some(pv => pv.id === id));
        let checkoutLink = `/shop/discount/${discount}`;

        if (validProductsIds.length > 0) {
          checkoutLink = `/shop/cart/${validProductsIds
            .map(id => `${id}:1`)
            .join(',')}?discount=${discount}`;
        }

        router.push(checkoutLink);
      }
    }

    if (add && productVariants) {
      const productIds = add.split(',');
      const validProductsIds = productIds.filter(
        id =>
          productVariants.some(pv => pv.id === id) &&
          !subscription?.items?.some(item => item.variantId === id),
      );

      if (validProductsIds.length > 0) {
        setPendingChanges(prev => {
          if (!prev) return null;

          const updatedProducts: ManageProductType[] = validProductsIds
            .map(productId => {
              const productVariant = productVariants.find(variant => variant.id === productId);
              return transformVariantToProduct({
                variant: productVariant as ProductVariant,
                supplement: supplements.find(
                  supplement => supplement.variantTid === productVariant?.tid,
                ),
              });
            })
            .filter(Boolean) as ManageProductType[];

          const highestPricedSupplementTid = getHighestPricedSupplementTid({
            selectedVariants: updatedProducts,
            activeSubscription: true,
            customerType: prev.customerType || 'subscription',
            productVariants,
          });

          const finalUpdatedProducts = updatedProducts.map(product => ({
            ...product,
            membershipIncluded: highestPricedSupplementTid === product.variantId,
          }));

          return {
            ...prev,
            products: [...prev.products, ...finalUpdatedProducts],
            addProducts: [...prev.addProducts, ...validProductsIds],
            customerType: prev.customerType || 'subscription',
          };
        });
        setSelectedSupplements(prev => ({
          ...prev,
          ...validProductsIds.reduce((acc: Record<string, boolean>, id) => {
            acc[id] = true;
            return acc;
          }, {} as Record<string, boolean>),
        }));
      }

      if (discount) {
        const hasDiscount = subscription?.discounts?.some(
          subscriptionDiscount =>
            subscriptionDiscount.title.toUpperCase() === discount.toUpperCase(),
        );
        hasDiscount && validProductsIds.length === 0
          ? setAlreadyAppliedPromoCodeError(true)
          : updatePromoCodes(discount, PromoCodeAction.ADD);
        setHasAutoAppliedPromoCodes(true);
      }
      hasActiveSubscriptionOrMembership && setOpenUpdateConfirmationModal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const handleOpenCancelationModal = () => {
    track<ModalViewEvent>({
      evtName: EventType.ModalView,
      modalName: 'Entered the Cancelation Flow',
    });
    setOpenCancelationModal(true);
  };

  return (
    <>
      {loading || productLoading ? (
        <LoadingSkeleton />
      ) : (
        productVariants && (
          <div>
            <div className={css.SubscriptionCardContainer}>
              <div className={css.SubscriptionCard}>
                <SubscriptionManagerCard
                  customerType="subscription"
                  state={
                    (!!pendingChanges?.hasActiveSubscription ||
                      pendingChanges?.subscriptionStatus === SubscriptionStatusEnum.PAUSED) &&
                    !pendingChanges?.hasMembership
                      ? pendingChanges?.state
                      : SubscriptionManagerState.DESELECTED
                  }
                  productVariants={productVariants}
                  initialState={initialState}
                  setInitialState={setInitialState}
                  pendingChanges={pendingChanges}
                  setPendingChanges={setPendingChanges}
                  onChangeSubscription={onChangeSubscription}
                  onCancelPlan={handleOpenCancelationModal}
                />
                {(!!pendingChanges?.hasActiveSubscription ||
                  pendingChanges?.subscriptionStatus === SubscriptionStatusEnum.PAUSED) &&
                  !pendingChanges?.hasMembership && (
                    <div>
                      <Text
                        as="h4"
                        variant="caption"
                        color="burgundy"
                        onClick={handleOpenCancelationModal}
                        textAlign="center"
                        className={css.CancelPlanButton}>
                        Cancel my plan
                      </Text>
                      {openCancelationModal && (
                        <CancelationModal
                          productVariants={productVariants}
                          open={openCancelationModal}
                          onOpenChange={setOpenCancelationModal}
                          hasAppliedDiscountCancelation={hasAppliedDiscountCancelation}
                          subscriptionItems={subscription?.items ?? []}
                          subscriptionEmail={subscription?.email || ''}
                        />
                      )}
                    </div>
                  )}
              </div>
              <div className={css.SubscriptionCard}>
                <SubscriptionManagerCard
                  customerType="membership"
                  state={
                    !!pendingChanges?.hasMembership
                      ? pendingChanges?.state
                      : SubscriptionManagerState.DESELECTED
                  }
                  productVariants={productVariants}
                  initialState={initialState}
                  setInitialState={setInitialState}
                  pendingChanges={pendingChanges}
                  setPendingChanges={setPendingChanges}
                  onChangeSubscription={onChangeSubscription}
                  onCancelPlan={handleOpenCancelationModal}
                />
                {!!pendingChanges?.hasMembership && pendingChanges?.hasActiveSubscription && (
                  <div>
                    <Text
                      as="h4"
                      variant="caption"
                      color="burgundy"
                      onClick={handleOpenCancelationModal}
                      textAlign="center"
                      className={css.CancelPlanButton}>
                      Cancel my plan
                    </Text>
                    {openCancelationModal && (
                      <CancelationModal
                        productVariants={productVariants}
                        open={openCancelationModal}
                        onOpenChange={setOpenCancelationModal}
                        hasAppliedDiscountCancelation={hasAppliedDiscountCancelation}
                        subscriptionItems={subscription?.items ?? []}
                        subscriptionEmail={subscription?.email || ''}
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
            <div>
              <AdditionalProducts />
            </div>
          </div>
        )
      )}
    </>
  );
};
